import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, History } from "lucide-react";
import { getAuditLog, type AuditLogEntry } from "@/api/auditLog";
import { ActivityDiffCell } from "@/features/admin/components/ActivityDiffCell";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { formatDateTime } from "@/lib/format";
import { useTenantTimezone } from "@/features/settings/hooks/useTenantTimezone";
import { cn } from "@/lib/cn";

/** Entries shown before «Показать все N записей» is offered (§5.4). */
const COLLAPSED_LIMIT = 3;

interface Props {
  orderId: number;
  /** Whether the surrounding dialog is open — gates the query. */
  enabled: boolean;
}

/**
 * «ЖУРНАЛ ИЗМЕНЕНИЙ» — the third column of the order detail modal.
 *
 * Two contracts this component exists to hold:
 *
 * 1. **It is open by default for every staff role, dispatchers included.** The
 *    old code gated the query behind `isCompanyAdmin && showAuditLog`, so a
 *    dispatcher never saw it and an admin saw it only after clicking to reveal.
 *    The backend widened its role check this sprint (scoped to a model
 *    whitelist, still tenant-scoped), so the server is the gate and the client
 *    must not re-implement one.
 * 2. **N in «Показать все N записей» is the SERVER's `count`**, never
 *    `results.length`. The loaded page is capped by DRF pagination, so counting
 *    it would under-report the moment an order has more history than one page —
 *    the BUG-274..278 defect class, where a label states a number the system
 *    does not actually mean.
 */
export function OrderAuditColumn({ orderId, enabled }: Props) {
  const { t } = useTranslation();
  // TASK-5043: audit stamps render in the tenant's zone. Safe as a hook here —
  // this component already calls `useQuery`, so it cannot render outside a
  // `QueryClientProvider`, and it has no provider-less test of its own.
  const tenantTz = useTenantTimezone();
  const [expanded, setExpanded] = useState(false);

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ["audit-log", "Order", orderId],
    queryFn: () =>
      getAuditLog({ model_name: "orders.Order", object_id: orderId }),
    enabled: enabled && orderId > 0,
    staleTime: 60 * 1000,
  });

  if (isLoading) {
    return (
      <div className="space-y-3" data-testid="audit-log-loading">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-full" />
        ))}
      </div>
    );
  }

  // The column stays mounted on failure — a sidebar that silently vanishes
  // leaves a two-column layout and no explanation (§5.4).
  if (isError) {
    return (
      <div
        role="alert"
        className="flex flex-col items-start gap-2 rounded-xl border border-brand-danger/30 bg-brand-danger/5 px-3 py-3"
      >
        <p className="flex items-center gap-2 text-sm font-medium text-brand-danger">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          {t("auditLog.loadFailed")}
        </p>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={isFetching}
          onClick={() => void refetch()}
        >
          {t("common.retry")}
        </Button>
      </div>
    );
  }

  const entries = data?.results ?? [];
  // The server total, not the loaded page. See the contract note above.
  const total = data?.count ?? 0;

  if (total === 0) {
    return (
      <EmptyState
        icon={History}
        title={t("auditLog.emptyTitle")}
        subtitle={t("auditLog.emptyBody")}
        subtitleClassName="max-w-[16rem] text-center"
      />
    );
  }

  const visible = expanded ? entries : entries.slice(0, COLLAPSED_LIMIT);
  const hasMore = !expanded && total > visible.length;

  return (
    /*
      One of the dialog's TWO permitted scrollers (the other is the parcels
      list) — owner, 2026-08-25: «если много записей, эта часть журнала должна
      скроллиться, а то когда записей много оно просто тянет модалку».

      The scroll sits on the `<ol>`, not on this wrapper, so «Показать все N
      записей» stays visible below the entries instead of scrolling away with
      them — the control that reveals the rest must not itself need revealing.

      `min-h-0` on both is load-bearing: a flex item's automatic minimum size
      would otherwise refuse to shrink below the full list, and the column would
      grow and stretch the dialog, which is the defect being fixed. The height is
      the column's leftover space rather than a fixed number, so the log fills
      whatever ДОСТАВКА and КУДА leave and «МАРШРУТ» still lands on the bottom
      edge; an order with 2 entries and one with 37 therefore produce the same
      dialog height.
    */
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <ol
        data-testid="audit-log-scroll"
        className="min-h-0 flex-1 space-y-3 overflow-y-auto overflow-x-hidden"
      >
        {visible.map((entry) => (
          <AuditEntryRow key={entry.id} entry={entry} tenantTz={tenantTz} />
        ))}
      </ol>

      {hasMore && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="text-sm font-medium text-brand-primary hover:underline"
        >
          {/*
            `count` is the SERVER total. Passing `entries.length` here would
            render a smaller, wrong number as soon as the history exceeds one
            page — and would still look correct in any test whose fixture fits
            on one page.
          */}
          {t("auditLog.showAll", { count: total })}
        </button>
      )}
    </div>
  );
}

/** One change-log entry: a status dot, a summary, then «actor · when». */
function AuditEntryRow({
  entry,
  tenantTz,
}: {
  entry: AuditLogEntry;
  tenantTz: string;
}) {
  const { t } = useTranslation();

  return (
    <li className="flex min-w-0 gap-2.5">
      <span
        className={cn(
          "mt-1.5 h-2 w-2 shrink-0 rounded-full",
          entry.action === "create" && "bg-brand-success",
          entry.action === "update" && "bg-brand-primary",
          entry.action === "delete" && "bg-brand-danger",
        )}
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900">
          {t(`auditLog.summary.${entry.action}`)}
        </p>
        {/*
          Owner, 2026-08-24: «Заказ изменён» alone says something happened without
          saying WHAT — the column was a list of identical lines. The diff is
          already in the payload (`AuditLogEntry.diff`), it simply was not rendered.

          Reusing the admin Activity Log's renderer rather than writing a second
          one: it already maps field names to localized labels, localizes status
          enums (`in_route` → «В маршруте»), formats booleans/dates/empties and
          suppresses `updated_at` noise. A private copy here would drift from the
          admin view the first time either changed — the two must agree, since they
          render the same audit rows.
        */}
        <div className="mt-1">
          <ActivityDiffCell diff={entry.diff ?? null} action={entry.action} />
        </div>
        <p className="mt-1 truncate text-xs text-gray-400">
          {entry.user ?? t("auditLog.system")}
          {" · "}
          {formatDateTime(entry.created_at, { timeZone: tenantTz })}
        </p>
      </div>
    </li>
  );
}
